import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageMeta from '../../components/base/PageMeta';
import JsonLd from '../../components/base/JsonLd';
import FloatingCtaBar from '../../components/feature/FloatingCtaBar';
import SignUpModal from '../../components/feature/SignUpModal';
import SiteFooter from '../../components/feature/SiteFooter';

const allComparisons = [
  {
    slug: 'wix-vs-wordpress',
    category: 'Website Builders',
    a: { name: 'Wix', icon: 'ri-layout-4-line', color: 'bg-amber-100 text-amber-700' },
    b: { name: 'WordPress', icon: 'ri-global-line', color: 'bg-gray-800 text-white' },
    criteria: [
      { label: 'Ease of Use', a: true, b: false },
      { label: 'Customization', a: false, b: true },
      { label: 'Pricing', a: true, b: true },
    ],
    views: 48200,
  },
  {
    slug: 'shopify-vs-woocommerce',
    category: 'E-commerce',
    a: { name: 'Shopify', icon: 'ri-store-2-line', color: 'bg-emerald-100 text-emerald-700' },
    b: { name: 'WooCommerce', icon: 'ri-shopping-bag-line', color: 'bg-violet-100 text-violet-600' },
    criteria: [
      { label: 'Setup Speed', a: true, b: false },
      { label: 'Cost Control', a: false, b: true },
      { label: 'Support', a: true, b: false },
    ],
    views: 39700,
  },
  {
    slug: 'notion-vs-clickup',
    category: 'Productivity',
    a: { name: 'Notion', icon: 'ri-file-text-line', color: 'bg-gray-100 text-gray-800' },
    b: { name: 'ClickUp', icon: 'ri-checkbox-multiple-line', color: 'bg-pink-100 text-pink-600' },
    criteria: [
      { label: 'Flexibility', a: true, b: false },
      { label: 'Features', a: false, b: true },
      { label: 'Learning Curve', a: true, b: false },
    ],
    views: 31500,
  },
  {
    slug: 'figma-vs-sketch',
    category: 'Design Tools',
    a: { name: 'Figma', icon: 'ri-pen-nib-line', color: 'bg-orange-100 text-orange-600' },
    b: { name: 'Sketch', icon: 'ri-artboard-line', color: 'bg-yellow-100 text-yellow-700' },
    criteria: [
      { label: 'Collaboration', a: true, b: false },
      { label: 'Performance', a: false, b: true },
      { label: 'Cross-platform', a: true, b: false },
    ],
    views: 27900,
  },
  {
    slug: 'mailchimp-vs-convertkit',
    category: 'Email Marketing',
    a: { name: 'Mailchimp', icon: 'ri-mail-send-line', color: 'bg-yellow-100 text-yellow-700' },
    b: { name: 'ConvertKit', icon: 'ri-send-plane-line', color: 'bg-orange-100 text-orange-600' },
    criteria: [
      { label: 'Beginner Friendly', a: true, b: false },
      { label: 'Creative Flows', a: false, b: true },
      { label: 'Automation', a: false, b: true },
    ],
    views: 22300,
  },
  {
    slug: 'ahrefs-vs-semrush',
    category: 'SEO Tools',
    a: { name: 'Ahrefs', icon: 'ri-bar-chart-line', color: 'bg-orange-100 text-orange-700' },
    b: { name: 'SEMrush', icon: 'ri-pie-chart-2-line', color: 'bg-red-100 text-red-600' },
    criteria: [
      { label: 'Backlink Data', a: true, b: false },
      { label: 'AI Content', a: false, b: true },
      { label: 'All-in-one', a: false, b: true },
    ],
    views: 35100,
  },
  {
    slug: 'slack-vs-microsoft-teams',
    category: 'Communication',
    a: { name: 'Slack', icon: 'ri-slack-line', color: 'bg-violet-100 text-violet-600' },
    b: { name: 'Microsoft Teams', icon: 'ri-team-line', color: 'bg-sky-100 text-sky-700' },
    criteria: [
      { label: 'User Experience', a: true, b: false },
      { label: 'Office Integration', a: false, b: true },
      { label: 'Pricing', a: false, b: true },
    ],
    views: 29600,
  },
  {
    slug: 'canva-vs-adobe-express',
    category: 'Design Tools',
    a: { name: 'Canva', icon: 'ri-magic-line', color: 'bg-teal-100 text-teal-600' },
    b: { name: 'Adobe Express', icon: 'ri-adobe-line', color: 'bg-red-100 text-red-600' },
    criteria: [
      { label: 'Templates Count', a: true, b: false },
      { label: 'Advanced Tools', a: false, b: true },
      { label: 'Ease of Use', a: true, b: false },
    ],
    views: 18700,
  },
];

const tabs = ['All', 'Website Builders', 'E-commerce', 'Productivity', 'Design Tools', 'Email Marketing', 'SEO Tools', 'Communication'];

const subLabels: Record<string, string> = {
  'Website Builders': 'Website Platforms',
  'E-commerce': 'Online Store Tools',
  'Productivity': 'Work Management',
  'Design Tools': 'Design Platforms',
  'Email Marketing': 'Marketing Tools',
  'SEO Tools': 'SEO Platforms',
  'Communication': 'Team Communication',
};

export default function ComparisonsPage() {
  const { i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState('All');
  const [helpEmail, setHelpEmail] = useState('');
  const [helpSent, setHelpSent] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const currentLang = i18n.language === 'vi' ? 'VI' : 'EN';

  const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://example.com';

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Tool Comparisons – Side-by-Side Software Analysis | 247digitrend',
    url: `${siteUrl}/comparisons`,
    description:
      'Side-by-side comparisons of the most popular digital tools. Wix vs WordPress, Shopify vs WooCommerce, Notion vs ClickUp, Figma vs Sketch and more.',
    isPartOf: { '@id': `${siteUrl}/#website` },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Comparisons', item: `${siteUrl}/comparisons` },
      ],
    },
    mainEntity: {
      '@type': 'ItemList',
      name: 'Tool Comparisons',
      numberOfItems: allComparisons.length,
      itemListElement: allComparisons.map((comp, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'Article',
          name: `${comp.a.name} vs ${comp.b.name}`,
          url: `${siteUrl}/comparisons/${comp.slug}`,
        },
      })),
    },
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) setLangOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filtered = activeTab === 'All' ? allComparisons : allComparisons.filter((c) => c.category === activeTab);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter', sans-serif" }}>
      <PageMeta
        title="Tool Comparisons – Wix vs WordPress, Shopify vs WooCommerce & More | 247digitrend"
        description="Honest side-by-side comparisons of leading digital tools. Compare features, pricing and performance. Wix vs WordPress, Ahrefs vs SEMrush, Slack vs Teams and 5+ more."
        canonical={`${siteUrl}/comparisons`}
        keywords="software comparison, Wix vs WordPress, Shopify vs WooCommerce, Ahrefs vs SEMrush, compare247"
      />
      <JsonLd data={pageSchema} />
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap" rel="stylesheet" />

      {/* ── SIGN UP MODAL ── */}
      <SignUpModal isOpen={showSignUp} onClose={() => setShowSignUp(false)} />

      {/* ── HEADER ── */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-sm border-b border-gray-100' : 'bg-white'}`}>
        <div className="mx-auto px-8 py-2 flex items-center justify-between">
          <a href="/" className="flex items-center cursor-pointer">
            <img src="https://storage.readdy-site.link/project_files/3785ac0c-31ac-4f9e-b2ab-27ad3777db2a/7544398a-500f-41a6-81c3-aa7efbf1c0d2_logo-247.png?v=04135332188f9bc8b00780c66cd5837d" alt="247compare" className="h-36 w-auto object-contain" />
          </a>
          <nav className="hidden md:flex items-center gap-8">
            <a href="/" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors cursor-pointer">Home</a>
            <a href="/categories" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors cursor-pointer">Categories</a>
            <a href="/reviews" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors cursor-pointer">Reviews</a>
            <a href="/comparisons" className="text-[19px] font-semibold text-violet-600 cursor-pointer">Comparisons</a>
            <a href="/blog" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors cursor-pointer">Blog</a>
          </nav>
          <div className="flex items-center gap-3">
            <div className="relative" ref={langRef}>
              <button onClick={() => setLangOpen(!langOpen)} className="flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 transition-colors rounded-full px-3 py-1.5 cursor-pointer">
                <i className="ri-translate-2 text-xs text-gray-500"></i>
                <span className="text-xs font-semibold text-gray-700">{currentLang}</span>
                <i className={`ri-arrow-down-s-line text-xs text-gray-400 transition-transform ${langOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {langOpen && (
                <div className="absolute right-0 top-full mt-1.5 bg-white rounded-xl border border-gray-100 overflow-hidden w-32" style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
                  {(['VI', 'EN'] as const).map((lang) => (
                    <button key={lang} onClick={() => { i18n.changeLanguage(lang.toLowerCase()); setLangOpen(false); }} className={`w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold transition-colors cursor-pointer ${currentLang === lang ? 'bg-violet-50 text-violet-700' : 'text-gray-600 hover:bg-gray-50'}`}>
                      <span>{lang === 'VI' ? '🇻🇳' : '🇺🇸'}</span>
                      <span>{lang === 'VI' ? 'Tiếng Việt' : 'English'}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button onClick={() => setShowSignUp(true)} className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-semibold px-5 py-2 rounded-full whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity">
              Sign Up
            </button>
          </div>
        </div>
      </header>

      {/* ── HERO ── */}
      <section className="pt-44 pb-12" style={{ background: 'linear-gradient(160deg, #faf5ff 0%, #fdf2ff 50%, #fff0fb 100%)' }}>
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h1 className="text-5xl font-black text-gray-900 mb-4 tracking-tight leading-tight">
            Tool{' '}
            <span
              className="text-transparent bg-clip-text"
              style={{ backgroundImage: 'linear-gradient(90deg, #9333ea, #c026d3, #e879f9)' }}
            >
              Comparisons
            </span>
          </h1>
          <p className="text-base text-gray-500 leading-relaxed max-w-xl mx-auto">
            Side-by-side comparisons to help you choose the right tool. Compare features, pricing, and performance.
          </p>
        </div>
      </section>

      {/* ── STICKY TABS ── */}
      <div className={`sticky top-[61px] z-40 bg-white border-b border-gray-100 transition-shadow ${scrolled ? 'shadow-sm' : ''}`}>
        <div className="max-w-6xl mx-auto px-8">
          <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-hide">
            {tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`text-xs font-semibold px-4 py-2 rounded-full whitespace-nowrap transition-all cursor-pointer flex-shrink-0 ${
                  activeTab === tab
                    ? 'text-white'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
                style={activeTab === tab ? { background: 'linear-gradient(90deg, #9333ea, #c026d3)' } : {}}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── COMPARISONS GRID ── */}
      <section className="py-12 bg-white">
        <div className="max-w-6xl mx-auto px-8">

          {/* Sub-label */}
          {activeTab !== 'All' && (
            <div className="mb-6">
              <span className="text-xs font-semibold text-violet-600 bg-violet-50 border border-violet-100 rounded-full px-3 py-1">
                {subLabels[activeTab]}
              </span>
            </div>
          )}

          {/* Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filtered.map((comp, i) => (
              <div
                key={i}
                onClick={() => navigate(`/comparisons/${comp.slug}`)}
                className="group bg-white rounded-2xl p-5 border border-gray-100 hover:border-violet-200 transition-all cursor-pointer block"
                style={{ boxShadow: '0 1px 6px rgba(0,0,0,0.05)' }}
              >
                {/* VS header */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <div className={`w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-xl ${comp.a.color} text-lg`}>
                      <i className={comp.a.icon}></i>
                    </div>
                    <span className="text-sm font-bold text-gray-900 truncate">{comp.a.name}</span>
                  </div>
                  <div className="flex-shrink-0 flex flex-col items-center gap-0.5">
                    <span className="text-[10px] font-black text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">VS</span>
                  </div>
                  <div className="flex items-center gap-2 flex-1 min-w-0 justify-end">
                    <span className="text-sm font-bold text-gray-900 truncate">{comp.b.name}</span>
                    <div className={`w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-xl ${comp.b.color} text-lg`}>
                      <i className={comp.b.icon}></i>
                    </div>
                  </div>
                </div>

                {/* Criteria */}
                <div className="mb-4">
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Quick Comparison:</span>
                  <div className="mt-2 space-y-2">
                    {comp.criteria.map((c, ci) => (
                      <div key={ci} className="flex items-center">
                        <span className="text-xs text-gray-600 flex-1">{c.label}</span>
                        <div className="flex items-center gap-3">
                          <div className={`w-2 h-2 rounded-full ${c.a ? 'bg-emerald-400' : 'bg-amber-300'}`}></div>
                          <div className={`w-2 h-2 rounded-full ${c.b ? 'bg-emerald-400' : 'bg-amber-300'}`}></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Footer row */}
                <div className="flex items-center justify-between pt-3 border-t border-gray-50">
                  <span className="text-xs text-gray-400">
                    <i className="ri-eye-line mr-1"></i>
                    {(comp.views / 1000).toFixed(1)}K views
                  </span>
                  <span className="text-xs font-semibold text-violet-600 inline-flex items-center gap-1 group-hover:translate-x-0.5 transition-transform whitespace-nowrap">
                    View Full Comparison <i className="ri-arrow-right-line text-xs"></i>
                  </span>
                </div>
              </div>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-20">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-gray-100">
                <i className="ri-file-search-line text-2xl text-gray-400"></i>
              </div>
              <p className="text-sm font-semibold text-gray-500">No comparisons found in this category</p>
            </div>
          )}
        </div>
      </section>

      {/* ── CTA BANNER ── */}
      <section className="py-14 mx-8 mb-14 rounded-3xl overflow-hidden" style={{ background: 'linear-gradient(135deg, #0d9488 0%, #7c3aed 50%, #db2777 100%)' }}>
        <div className="max-w-xl mx-auto px-6 text-center text-white">
          <h3 className="text-2xl font-black mb-3">Need Help Choosing the Right Tool?</h3>
          <p className="text-sm text-white/80 leading-relaxed mb-7">
            Get personalized recommendations based on your specific needs and budget. Our experts are here to help.
          </p>
          {helpSent ? (
            <div className="flex flex-col items-center gap-3">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white/20 border border-white/30">
                <i className="ri-check-line text-white text-xl"></i>
              </div>
              <p className="text-white font-semibold text-sm">Sent successfully! We&apos;ll reach out soon.</p>
            </div>
          ) : (
            <form
              data-readdy-form
              action="https://readdy.ai/api/form/d6v1655t4s7j0tv5iam0"
              method="POST"
              onSubmit={(e) => {
                e.preventDefault();
                const form = e.currentTarget;
                const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
                fetch('https://readdy.ai/api/form/d6v1655t4s7j0tv5iam0', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                  body: data.toString(),
                }).then(() => {
                  form.reset();
                  setHelpEmail('');
                  setHelpSent(true);
                });
              }}
              className="flex items-center gap-2 max-w-sm mx-auto"
            >
              <input
                type="email"
                name="email"
                required
                value={helpEmail}
                onChange={(e) => setHelpEmail(e.target.value)}
                placeholder="Enter your email..."
                className="flex-1 px-5 py-3 rounded-full text-sm text-gray-800 placeholder-gray-400 bg-white/90 outline-none border-none"
              />
              <button
                type="submit"
                className="bg-white text-violet-700 font-bold text-sm px-6 py-3 rounded-full whitespace-nowrap cursor-pointer hover:bg-violet-50 transition-colors"
              >
                Get Started
              </button>
            </form>
          )}
        </div>
      </section>

      {/* ── FOOTER ── */}
      <SiteFooter />

      <FloatingCtaBar onSignUp={() => setShowSignUp(true)} />
    </div>
  );
}
